import { db } from '../db';
import { createDocument, deleteDocument, saveDocument, setDocFolder } from '../library';
import { captureSnapshot } from '../snapshots';
import { normalizeFolderPath } from '../folders';
import { useStore } from '../../store/useStore';
import { t } from '../../i18n';
import { directoryFs } from './fsAccess';
import { checkGithubAccess, githubFs, type GithubRepo } from './githubFs';
import { nativeShell } from '../native/bridge';
import { nativeVaultFs } from '../native/vaultFs';
import { reconcile, type Manifest, type SyncLibrary, type SyncReport } from './reconcile';

/**
 * Folder sync: a chosen folder of `.md` files and the library, kept in step.
 *
 * The rules are in reconcile.ts; this file holds the state (the folder handle,
 * the manifest, the last result) and runs one sync at a time. It is deliberately
 * separate from the backup, which only ever writes: a sync reads the folder back
 * and changes documents, so it is something to turn on knowingly.
 */

export interface FolderSyncState {
  enabled: boolean;
  /** Where the files live: a folder on this device, or a GitHub repository. */
  target: 'folder' | 'github';
  folderName: string | null;
  lastSyncAt: number | null;
  lastReport: SyncReport | null;
  /** Why the last attempt did not run, if it did not. */
  lastProblem: 'permission' | 'error' | null;
}

const STATE_KEY = 'folderSyncState';
const DIR_KEY = 'folderSyncDir';
const GITHUB_KEY = 'folderSyncGithub';
const MANIFEST_KEY = 'folderSyncManifest';
const DEFAULTS: FolderSyncState = {
  enabled: false,
  target: 'folder',
  folderName: null,
  lastSyncAt: null,
  lastReport: null,
  lastProblem: null,
};

export function supportsFolderSync(): boolean {
  return (
    nativeShell() !== null || (typeof window !== 'undefined' && 'showDirectoryPicker' in window)
  );
}

/**
 * Inside a native shell the vault is not a choice: notes are kept as files in
 * the app's own document directory, always, because webview storage is not
 * something to trust a library to. There is nothing to pick and nothing to
 * switch off — only a place to see where the files are.
 */
export function nativeVault(): { label: string } | null {
  const shell = nativeShell();
  return shell ? { label: shell.vaultLabel } : null;
}

export async function getFolderSyncState(): Promise<FolderSyncState> {
  const row = await db.kv.get(STATE_KEY);
  const stored = { ...DEFAULTS, ...((row?.value as Partial<FolderSyncState> | undefined) ?? {}) };
  const native = nativeVault();
  // In a shell the vault is always on, whatever an earlier browser session stored.
  return native ? { ...stored, enabled: true, folderName: native.label } : stored;
}

async function saveState(patch: Partial<FolderSyncState>): Promise<FolderSyncState> {
  const next = { ...(await getFolderSyncState()), ...patch };
  await db.kv.put({ key: STATE_KEY, value: next });
  return next;
}

/**
 * The repository this device syncs with, token included.
 *
 * It is kept here and nowhere else: the token is not a document, so it is not
 * synced, not exported and not part of a backup. Another device gets its own.
 */
export async function getGithubConfig(): Promise<GithubRepo | null> {
  return ((await db.kv.get(GITHUB_KEY))?.value as GithubRepo | undefined) ?? null;
}

/**
 * Point the sync at a repository, after checking that the token can actually
 * reach that branch — a wrong token is worth saying now rather than as a
 * failed sync later. Connecting starts a fresh pairing.
 */
export async function connectGithub(
  config: GithubRepo,
): Promise<{ ok: true; state: FolderSyncState } | { ok: false; reason: string }> {
  const check = await checkGithubAccess(config);
  if (!check.ok) return { ok: false, reason: check.reason };
  await db.kv.put({ key: GITHUB_KEY, value: config });
  await db.kv.delete(MANIFEST_KEY);
  return {
    ok: true,
    state: await saveState({
      enabled: true,
      target: 'github',
      folderName: `${config.repo} (${config.branch})`,
      lastReport: null,
      lastProblem: null,
    }),
  };
}

async function getHandle(): Promise<FileSystemDirectoryHandle | null> {
  return ((await db.kv.get(DIR_KEY))?.value as FileSystemDirectoryHandle | undefined) ?? null;
}

interface PermissionedHandle {
  queryPermission?: (o: { mode: 'readwrite' }) => Promise<PermissionState>;
  requestPermission?: (o: { mode: 'readwrite' }) => Promise<PermissionState>;
}

async function permitted(handle: FileSystemDirectoryHandle, prompt: boolean): Promise<boolean> {
  const h = handle as unknown as PermissionedHandle;
  const opts = { mode: 'readwrite' } as const;
  // Handles without the permission methods (the origin-private file system)
  // are always writable.
  if (!h.queryPermission || (await h.queryPermission(opts)) === 'granted') return true;
  if (!prompt || !h.requestPermission) return false;
  return (await h.requestPermission(opts)) === 'granted';
}

/**
 * Pick a folder and turn sync on. Starting over with a different folder starts
 * a fresh pairing: the old manifest describes files that are not in this one.
 */
export async function chooseSyncFolder(): Promise<FolderSyncState | null> {
  const pick = (
    window as {
      showDirectoryPicker?: (o: { mode: 'readwrite' }) => Promise<FileSystemDirectoryHandle>;
    }
  ).showDirectoryPicker;
  if (!pick) return null;
  let handle: FileSystemDirectoryHandle;
  try {
    handle = await pick({ mode: 'readwrite' });
  } catch {
    return null; // cancelled
  }
  await db.kv.put({ key: DIR_KEY, value: handle });
  await db.kv.delete(MANIFEST_KEY);
  return saveState({
    enabled: true,
    target: 'folder',
    folderName: handle.name,
    lastReport: null,
    lastProblem: null,
  });
}

/** Stop syncing. The folder and its files are left exactly as they are. */
export async function stopFolderSync(): Promise<FolderSyncState> {
  await db.kv.delete(DIR_KEY);
  await db.kv.delete(GITHUB_KEY);
  await db.kv.delete(MANIFEST_KEY);
  return saveState({
    enabled: false,
    target: 'folder',
    folderName: null,
    lastReport: null,
    lastProblem: null,
  });
}

/** The library as the sync sees it, applied through the normal write paths. */
const libraryAdapter: SyncLibrary = {
  async list() {
    return (await db.documents.toArray()).map((d) => ({
      id: d.id,
      title: d.title,
      content: d.content,
      folder: normalizeFolderPath(d.folder),
      updatedAt: d.updatedAt,
      deleted: d.deletedAt != null,
    }));
  },
  async create({ title, content, folder }) {
    const doc = await createDocument({ title, content });
    if (folder) await setDocFolder(doc.id, folder);
    return doc.id;
  },
  async update(id, content) {
    await saveDocument(id, { content });
    // The open document follows a change made on disk, like one from another device.
    useStore.getState().reloadOpenDoc?.(id);
  },
  trash: (id) => deleteDocument(id),
  setFolder: (id, folder) => setDocFolder(id, folder),
  async preserve(id, title, content) {
    await captureSnapshot({ id, title, content }, 'remote');
  },
};

let running: Promise<FolderSyncState> | null = null;

/**
 * Run one sync. `prompt` allows a permission prompt, which browsers only show
 * inside a click; automatic runs pass false and report a lapsed permission.
 */
export function runFolderSync(prompt = false): Promise<FolderSyncState> {
  running ??= (async () => {
    try {
      const state = await getFolderSyncState();
      const shell = nativeShell();
      // A repository needs no handle and no permission prompt: the token is
      // the permission, and it was checked when it was entered.
      const github = state.target === 'github' ? await getGithubConfig() : null;
      const handle = shell || github ? null : await getHandle();
      if (!state.enabled || (!shell && !github && !handle)) return state;
      // A shell writes inside its own app folder, so there is no permission to ask for.
      if (handle && !(await permitted(handle, prompt))) {
        return saveState({ lastProblem: 'permission' });
      }
      const fs = shell ? nativeVaultFs(shell) : github ? githubFs(github) : directoryFs(handle!);

      const previous = ((await db.kv.get(MANIFEST_KEY))?.value as Manifest | undefined) ?? {};
      try {
        const { manifest, report } = await reconcile(fs, libraryAdapter, previous);
        await db.kv.put({ key: MANIFEST_KEY, value: manifest });
        const first = report.conflicts[0];
        if (first) {
          useStore.getState().setSyncConflict({ docId: first.id });
          useStore.getState().setSyncNotice(
            t('folderSync.conflicts', {
              titles: report.conflicts.map((c) => c.title).join(', '),
            }),
          );
        }
        return saveState({ lastSyncAt: Date.now(), lastReport: report, lastProblem: null });
      } catch {
        return saveState({ lastProblem: 'error' });
      }
    } finally {
      running = null;
    }
  })();
  return running;
}

/** True while a sync is running, so its own writes do not schedule another. */
export function folderSyncRunning(): boolean {
  return running !== null;
}
